import { EventTarget } from 'cc';
import Delay from '../Toolkit/Utils/Delay';

enum EventType {
    ENTER_NEW_STAGE = 'ENTER_NEW_STAGE',
    SKIP_CURRENT_STAGE = 'SKIP_CURRENT_STAGE',
    ACCELERATE_CURRENT_STAGE = 'ACCELERATE_CURRENT_STAGE',
    RESET_STAGE = 'RESET_STAGE',
}

export enum EffectStageState {
    NORMAL,
    ACCELERATED,
    SKIPPED,
}

export default class EffectStageManager extends EventTarget {
    public static readonly EVENT_TYPE = EventType;

    private _stageIndex: number = 0;
    private _stageState: EffectStageState = EffectStageState.NORMAL;
    private _stageStateLock: boolean = false;
    private _onceSkipped: boolean = false;
    private _onceAccelerated: boolean = false;
    private _skipPromise: Promise<void> = null;
    private _skipResolve: () => void = null;
    private _acceleratePromise: Promise<void> = null;
    private _accelerateResolve: () => void = null;
    private _stateChangeCallbacks: Array<() => boolean> = [];

    public get stageIndex(): number {
        return this._stageIndex;
    }

    public get onceSkipped(): boolean {
        return this._onceSkipped;
    }

    public get onceAccelerated(): boolean {
        return this._onceAccelerated;
    }

    public get currentSkipped(): boolean {
        return this._stageState === EffectStageState.SKIPPED;
    }

    public get currentAccelerated(): boolean {
        return this._stageState === EffectStageState.ACCELERATED;
    }

    /**
     * @returns a promise that resolves when the current stage is skipped
     */
    public next(lockStageState: boolean = false): void {
        this._stageIndex += 1;
        this._stageState = EffectStageState.NORMAL;
        this._stageStateLock = lockStageState;
        this._skipPromise = new Promise<void>((resolve) => {
            this._skipResolve = resolve;
        });
        this._acceleratePromise = new Promise<void>((resolve) => {
            this._accelerateResolve = resolve;
        });
        this.emit(EventType.ENTER_NEW_STAGE, this._stageIndex);
    }

    public skip(): void {
        if (this.currentSkipped || this._stageStateLock) {
            return;
        }
        this._stageState = EffectStageState.SKIPPED;
        this._onceSkipped = true;
        this._skipResolve?.();
        this._invokeStateChangeCallbacks();
        this.emit(EventType.SKIP_CURRENT_STAGE, this._stageIndex);
    }

    public accelerate(): void {
        if (this.currentSkipped || this.currentAccelerated || this._stageStateLock) {
            return;
        }
        this._stageState = EffectStageState.ACCELERATED;
        this._onceAccelerated = true;
        this._accelerateResolve?.();
        this._invokeStateChangeCallbacks();
        this.emit(EventType.ACCELERATE_CURRENT_STAGE, this._stageIndex);
    }

    public toggleStateLock(lock: boolean): void {
        if (this._stageStateLock === lock) {
            return;
        }
        this._stageStateLock = lock;
    }

    /**
     * Registers a callback to be invoked whenever the stage state changes.
     * @param callback returns true if the callback should be removed after being called, false to keep it
     */
    public registerForStateChange(callback: () => boolean): void {
        this._stateChangeCallbacks.push(callback);
        if (this.currentSkipped || this.currentAccelerated) {
            this._invokeStateChangeCallbacks();
        }
    }

    public async waitForNextStage(): Promise<void> {
        await Delay.event(EventType.ENTER_NEW_STAGE, this);
    }

    public async waitForCurrentStageSkip(): Promise<void> {
        if (this.currentSkipped) {
            return;
        }
        const result = await Promise.race([this._skipPromise.then(() => true), Delay.event(EventType.ENTER_NEW_STAGE, this).then(() => false)]);
        if (result) {
            return;
        }
        // never resolve since a new stage has entered
        await Delay.forever();
    }

    public async waitForCurrentStageAccelerate(): Promise<void> {
        if (this.currentSkipped || this.currentAccelerated) {
            return;
        }
        const result = await Promise.race([this._acceleratePromise.then(() => true), Delay.event(EventType.ENTER_NEW_STAGE, this).then(() => false)]);
        if (result) {
            return;
        }
        // never resolve since a new stage has entered
        await Delay.forever();
    }

    public waitForReset() {
        return Delay.event(EventType.RESET_STAGE, this);
    }

    public reset(): void {
        this._stageIndex = 0;
        this._stageState = EffectStageState.NORMAL;
        this._stageStateLock = false;
        this._onceSkipped = false;
        this._onceAccelerated = false;
        this._skipPromise = null;
        this._skipResolve = null;
        this._acceleratePromise = null;
        this._accelerateResolve = null;
        this._stateChangeCallbacks = [];
        this.emit(EventType.RESET_STAGE);
        this.emit(EventType.ENTER_NEW_STAGE, this._stageIndex);
    }

    private _invokeStateChangeCallbacks(): void {
        this._stateChangeCallbacks = this._stateChangeCallbacks.filter((callback) => {
            const shouldRemove = callback();
            return !shouldRemove;
        });
    }
}
